import { HttpStatusCode } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import {
  ApiException,
  AuditLogApi,
  auditLogsAtomFamily,
  auditLogsErrorAtomFamily,
  errors,
  IAuditLogDto,
  Log,
  normalizeAuditLogs,
  RydeUiUnit,
  shouldRedirectToLoginForCaughtApiException,
  showTokenExpiredAlert,
  storeCurrentPageAndRedirectToLogin,
  useFormats,
  warnAndToastException,
} from "@ryde/shared";

import { useDropdownSourceContext } from "../contexts";
import { loadAuditLogAreaNames } from "./use-audit-log.utils";

export const useAuditLog = (entity: RydeUiUnit) => {
  const { t } = useTranslation();
  const { formatDateTimeNormalized } = useFormats();
  const { getDropdownItems } = useDropdownSourceContext();
  const [auditLogs, setAuditLogs] = useRecoilState(auditLogsAtomFamily(entity));
  const [error, setError] = useRecoilState(auditLogsErrorAtomFamily(entity));
  const [loading, setLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchAuditLogById = useCallback(
    async (id: number): Promise<void> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!id) {
        Log.warn("Invalid id for fetching audit log");
        return;
      }

      if (!entity) {
        Log.warn("Invalid entity for fetching audit log");
        return;
      }

      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError(undefined);

      try {
        const currentController = abortControllerRef.current;
        const [auditLogApiResponse, areaNameById] = await Promise.all([
          AuditLogApi.getLogById({
            id,
            entity,
            abortController: currentController,
          }),
          loadAuditLogAreaNames({ entity, getDropdownItems }),
        ]);

        // Check if request was aborted
        if (currentController?.signal.aborted) {
          return;
        }

        const auditLogsList: IAuditLogDto[] = auditLogApiResponse ?? [];
        const auditLogsNormalized = normalizeAuditLogs({
          auditLogs: auditLogsList,
          formatDateTimeNormalized,
          areaNameById,
        });
        Log.debug(
          "Audit logs fetched and normalized successfully",
          auditLogsNormalized,
        );
        setAuditLogs(auditLogsNormalized);

        setError(undefined);
        setLoading(false);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          setLoading(false);
          return;
        }

        if ((err as ApiException)?.statusCode === HttpStatusCode.Unauthorized) {
          if (shouldRedirectToLoginForCaughtApiException(err)) {
            showTokenExpiredAlert();
            storeCurrentPageAndRedirectToLogin();
            setLoading(false);
            return;
          }
          setLoading(false);
          setError(t(errors.common.authenticationError));
          return;
        }

        const translatableItem = warnAndToastException({
          error: err,
          fallbackTranslationKey: errors.common.failedToFetchAuditLog,
          translationFunction: t,
        });

        setLoading(false);
        setError(
          t(
            translatableItem.translationKey,
            translatableItem.translationParams,
          ),
        );
      }
    },
    [
      t,
      formatDateTimeNormalized,
      getDropdownItems,
      entity,
      setAuditLogs,
      setError,
      setLoading,
    ],
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    auditLogs,
    loading,
    error,
    fetchAuditLogById,
  };
};
